import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocale } from '@/contexts/LocaleContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  Search, Loader2, Upload, X, Palette, Shirt, Image, 
  ExternalLink, Copy, Eye, Package, Layers, History, Clock,
  Wand2, Plus, Save, Tag, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fetchWithAuth = async (url, options = {}) => {
  const token = localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token');
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': token ? `Bearer ${token}` : '',
    }
  });
};

const PRODUCT_CATEGORIES = [
  { id: 'telas', name: 'Telas', nameEn: 'Fabrics' },
  { id: 'hilos', name: 'Hilos', nameEn: 'Threads' },
  { id: 'botones', name: 'Botones', nameEn: 'Buttons' },
  { id: 'cierres', name: 'Cierres', nameEn: 'Zippers' },
  { id: 'accesorios', name: 'Accesorios', nameEn: 'Accessories' },
  { id: 'maquinaria', name: 'Maquinaria', nameEn: 'Machinery' },
  { id: 'prendas', name: 'Prendas', nameEn: 'Garments' },
  { id: 'otros', name: 'Otros', nameEn: 'Other' },
];

const VisualSearch = ({ onSendToOptimizer, initialImage, onImageProcessed, onCreditError, onSendToTexChat }) => {
  const { isUSA } = useLocale();
  
  // State
  const [activeTab, setActiveTab] = useState('search'); // 'search' or 'history'
  const [searchImage, setSearchImage] = useState(null);
  const [searchPreview, setSearchPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [imageModal, setImageModal] = useState(null);
  
  // History state
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Add to catalog state
  const [showAddCatalog, setShowAddCatalog] = useState(false);
  const [catalogForm, setCatalogForm] = useState({
    name: '',
    description: '',
    category: '',
    composition: '',
    colors: [],
    price: '',
    unit: 'metro'
  });
  const [savingProduct, setSavingProduct] = useState(false);
  
  const fileInputRef = useRef(null);

  // Fetch search history
  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/texvision/visual-search/history?limit=20`);
      if (res.ok) {
        const data = await res.json();
        setHistory(data);
      }
    } catch (e) {
      console.error('Error fetching history:', e);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  // Load history when switching to history tab
  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab, fetchHistory]);

  // Load initial image from Photo Optimizer via sessionStorage (runs only on mount)
  useEffect(() => {
    const optimizerData = sessionStorage.getItem('optimizer_to_search');
    if (optimizerData) {
      try {
        const data = JSON.parse(optimizerData);
        if (data.image_base64) {
          setSearchImage(data.image_base64);
          setSearchPreview(`data:image/png;base64,${data.image_base64}`);
          setResult(null);
          setActiveTab('search');
          toast.success('¡Imagen cargada! Haz clic en Buscar para analizar.');
          sessionStorage.removeItem('optimizer_to_search');
        }
      } catch (e) {
        console.error('Error loading image from optimizer:', e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount

  // Load initial image from props (for backwards compatibility)
  useEffect(() => {
    if (initialImage) {
      setSearchImage(initialImage);
      setSearchPreview(`data:image/png;base64,${initialImage}`);
      setResult(null);
      setActiveTab('search');
      toast.success(isUSA ? 'Image loaded! Click Search to analyze.' : '¡Imagen cargada! Haz clic en Buscar para analizar.');
      if (onImageProcessed) {
        onImageProcessed();
      }
    }
  }, [initialImage, onImageProcessed, isUSA]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(isUSA ? 'Image must be less than 10MB' : 'La imagen debe ser menor a 10MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result.split(',')[1];
        setSearchImage(base64);
        setSearchPreview(e.target.result);
        setResult(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSearch = async () => {
    if (!searchImage) {
      toast.error(isUSA ? 'Please upload an image' : 'Por favor sube una imagen');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      // First, analyze the image for colors and characteristics
      const analysisRes = await fetchWithAuth(`${API_URL}/api/texvision/visual-search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_base64: searchImage,
          search_type: 'all'
        })
      });

      const analysisData = await analysisRes.json();
      
      if (!analysisRes.ok) {
        throw new Error(analysisData.detail || 'Error al analizar');
      }

      // Then, search for visually similar products using AI comparison
      toast.info(isUSA ? 'Searching for similar products...' : 'Buscando productos similares...');
      
      const similarRes = await fetchWithAuth(`${API_URL}/api/texvision/visual-search/find-similar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_base64: searchImage,
          search_type: 'visual'
        })
      });

      const similarData = await similarRes.json();
      
      // Combine results
      let finalProducts = [];
      
      if (similarRes.ok && similarData.similar_products?.length > 0) {
        finalProducts = similarData.similar_products;
      } else if (analysisData.similar_products?.length > 0) {
        finalProducts = analysisData.similar_products;
      }

      setResult({
        analysis: analysisData.analysis,
        similar_products: finalProducts,
        search_id: analysisData.search_id,
        visual_search_description: similarData.search_image_description
      });
      
      // Refresh history
      fetchHistory();
      
      toast.success(isUSA ? 'Analysis complete!' : '¡Análisis completado!');
    } catch (e) {
      console.error('Visual search error:', e);
      if (onCreditError && onCreditError(e.message)) {
        // Error was handled by parent
      } else {
        toast.error(e.message || (isUSA ? 'Search failed' : 'Error en la búsqueda'));
      }
    } finally {
      setLoading(false);
    }
  };

  const copyHexCode = (hex) => {
    navigator.clipboard.writeText(hex);
    toast.success(isUSA ? 'HEX code copied!' : '¡Código HEX copiado!');
  };

  const openPantoneFinder = (hex) => {
    copyHexCode(hex);
    window.open('https://www.pantone.com/color-finder', '_blank');
    toast.info(isUSA ? 'Paste the HEX code in the Pantone finder' : 'Pega el código HEX en el buscador de Pantone');
  };

  const resetSearch = () => {
    setSearchImage(null);
    setSearchPreview(null);
    setResult(null);
  };

  // Load a search from history
  const loadFromHistory = (historyItem) => {
    setResult({
      analysis: historyItem.analysis,
      similar_products: [],
      search_id: historyItem.search_id
    });
    setActiveTab('search');
    toast.info(isUSA ? 'Search loaded from history' : 'Búsqueda cargada del historial');
  };

  // Send image to Photo Optimizer
  const sendToOptimizer = () => {
    if (searchImage && onSendToOptimizer) {
      onSendToOptimizer(searchImage);
      toast.success(isUSA ? 'Image sent to Photo Optimizer' : 'Imagen enviada al Optimizador');
    }
  };

  // Open Add to Catalog dialog with pre-filled data
  const openAddToCatalog = () => {
    if (!result?.analysis) {
      toast.error(isUSA ? 'Please analyze an image first' : 'Por favor analiza una imagen primero');
      return;
    }
    
    const analysis = result.analysis;
    
    // Pre-fill form with detected data
    setCatalogForm({
      name: '',
      description: isUSA ? analysis.description_en : analysis.description_es || '',
      category: analysis.fabric_type ? 'telas' : (analysis.garment_type ? 'prendas' : ''),
      composition: analysis.fabric_type || '',
      colors: analysis.colors?.map(c => c.hex) || [],
      price: '',
      unit: 'metro'
    });
    
    setShowAddCatalog(true);
  };

  // Save product to catalog
  const handleSaveProduct = async () => {
    if (!catalogForm.name.trim()) {
      toast.error(isUSA ? 'Please enter a product name' : 'Por favor ingresa un nombre de producto');
      return;
    }

    setSavingProduct(true);
    try {
      // First check if user has a company
      const companyRes = await fetchWithAuth(`${API_URL}/api/companies/my-company`);
      
      if (!companyRes.ok) {
        toast.error(isUSA 
          ? 'You need to register a company first to add products' 
          : 'Necesitas registrar una empresa primero para agregar productos');
        setSavingProduct(false);
        return;
      }

      // Create product with image
      const productData = {
        name: catalogForm.name,
        description: catalogForm.description,
        category: catalogForm.category,
        composition: catalogForm.composition,
        available_colors: catalogForm.colors,
        price: catalogForm.price ? parseFloat(catalogForm.price) : null,
        unit: catalogForm.unit,
        images: searchPreview ? [searchPreview] : []
      };

      const res = await fetchWithAuth(`${API_URL}/api/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(productData)
      });

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Error al guardar');
      }

      toast.success(isUSA ? 'Product added to catalog!' : '¡Producto agregado al catálogo!');
      setShowAddCatalog(false);
      
      // Reset form
      setCatalogForm({
        name: '',
        description: '',
        category: '',
        composition: '',
        colors: [],
        price: '',
        unit: 'metro'
      });
    } catch (e) {
      console.error('Save product error:', e);
      toast.error(e.message || (isUSA ? 'Error saving product' : 'Error al guardar producto'));
    } finally {
      setSavingProduct(false);
    }
  };

  // Remove color from list
  const removeColor = (hexToRemove) => {
    setCatalogForm(prev => ({
      ...prev,
      colors: prev.colors.filter(c => c !== hexToRemove)
    }));
  };

  // Add color to list
  const addColor = (hex) => {
    if (!catalogForm.colors.includes(hex)) {
      setCatalogForm(prev => ({
        ...prev,
        colors: [...prev.colors, hex]
      }));
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="visual-search">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Search className="w-6 h-6 text-emerald-600" />
            {isUSA ? 'Visual Search' : 'Buscador Visual'}
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            {isUSA 
              ? 'Upload an image to detect colors and find similar products' 
              : 'Sube una imagen para detectar colores y encontrar productos similares'}
          </p>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2">
          <Button
            variant={activeTab === 'search' ? 'default' : 'outline'}
            onClick={() => setActiveTab('search')}
            className={activeTab === 'search' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
          >
            <Search className="w-4 h-4 mr-2" />
            {isUSA ? 'Search' : 'Buscar'}
          </Button>
          <Button
            variant={activeTab === 'history' ? 'default' : 'outline'}
            onClick={() => setActiveTab('history')}
            className={activeTab === 'history' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
          >
            <History className="w-4 h-4 mr-2" />
            {isUSA ? 'History' : 'Historial'}
            {history.length > 0 && (
              <Badge className="ml-2 bg-white/20 text-white">{history.length}</Badge>
            )}
          </Button>
        </div>
      </div>

      {/* Search Tab */}
      {activeTab === 'search' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Upload & Analysis */}
          <div className="space-y-6">
            {/* Upload Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  {isUSA ? 'Upload Image' : 'Subir Imagen'}
                </CardTitle>
                <CardDescription>
                  {isUSA 
                    ? 'Upload a photo of fabric, garment, or any textile to analyze' 
                    : 'Sube una foto de tela, prenda o cualquier textil para analizar'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  className="hidden"
                />
                
                {searchPreview ? (
                  <div className="space-y-4">
                    <div className="relative">
                      <img 
                        src={searchPreview} 
                        alt="Search preview" 
                        className="w-full h-64 object-contain rounded-lg border-2 border-emerald-200 bg-slate-50 cursor-pointer"
                        onClick={() => setImageModal({ src: searchPreview, title: isUSA ? 'Uploaded Image' : 'Imagen Subida' })}
                      />
                      <Button 
                        size="sm" 
                        variant="destructive" 
                        className="absolute top-2 right-2 w-8 h-8 p-0"
                        onClick={resetSearch}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <Button 
                      className="w-full bg-emerald-600 hover:bg-emerald-700 py-6 text-lg"
                      onClick={handleSearch}
                      disabled={loading}
                    >
                      {loading ? (
                        <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> {isUSA ? 'Analyzing...' : 'Analizando...'}</>
                      ) : (
                        <><Search className="w-5 h-5 mr-2" /> {isUSA ? 'Analyze Image' : 'Analizar Imagen'}</>
                      )}
                    </Button>

                    {/* Action buttons after upload */}
                    {searchImage && onSendToOptimizer && (
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={sendToOptimizer}
                      >
                        <Wand2 className="w-4 h-4 mr-2" />
                        {isUSA ? 'Send to Photo Optimizer' : 'Enviar a Optimizador de Fotos'}
                      </Button>
                    )}

                    {/* Ask TexChat AI */}
                    {searchImage && result && onSendToTexChat && (
                      <Button 
                        variant="outline" 
                        className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                        onClick={() => {
                          const contextData = {
                            fabric_type: result.analysis?.fabric_type || null,
                            garment_type: result.analysis?.garment_type || null,
                            colors: result.analysis?.colors?.map(c => c.name).join(', ') || null,
                            description: result.analysis?.description_es || result.analysis?.texture || ''
                          };
                          onSendToTexChat(contextData, searchImage);
                        }}
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        {isUSA ? 'Ask TexChat AI' : 'Preguntar a TexChat AI'}
                      </Button>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-64 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center gap-3 hover:border-emerald-400 hover:bg-emerald-50 transition-colors"
                  >
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
                      <Upload className="w-8 h-8 text-emerald-600" />
                    </div>
                    <div className="text-center">
                      <p className="font-medium text-slate-700">
                        {isUSA ? 'Click to upload an image' : 'Haz clic para subir una imagen'}
                      </p>
                      <p className="text-sm text-slate-500 mt-1">
                        {isUSA ? 'JPEG, PNG, WebP up to 10MB' : 'JPEG, PNG, WebP hasta 10MB'}
                      </p>
                    </div>
                  </button>
                )}
              </CardContent>
            </Card>

            {/* Color Analysis Results */}
            {result && result.analysis && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Palette className="w-5 h-5 text-purple-600" />
                      {isUSA ? 'Detected Colors' : 'Colores Detectados'}
                    </CardTitle>
                    {/* Add to Catalog Button */}
                    <Button 
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={openAddToCatalog}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      {isUSA ? 'Add to Catalog' : 'Agregar al Catálogo'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {result.analysis.colors?.map((color, idx) => (
                      <div 
                        key={idx} 
                        className="rounded-lg overflow-hidden border border-slate-200 hover:shadow-md transition-shadow"
                      >
                        <div 
                          className="h-16 w-full cursor-pointer"
                          style={{ backgroundColor: color.hex }}
                          onClick={() => copyHexCode(color.hex)}
                          title={isUSA ? 'Click to copy' : 'Clic para copiar'}
                        />
                        <div className="p-2 bg-white">
                          <p className="font-medium text-sm">{color.name}</p>
                          <div className="flex items-center justify-between mt-1">
                            <p className="text-xs text-slate-500 font-mono">{color.hex}</p>
                            <div className="flex gap-1">
                              <button
                                onClick={() => copyHexCode(color.hex)}
                                className="p-1 hover:bg-slate-100 rounded"
                                title={isUSA ? 'Copy HEX' : 'Copiar HEX'}
                              >
                                <Copy className="w-3 h-3 text-slate-400" />
                              </button>
                              <button
                                onClick={() => openPantoneFinder(color.hex)}
                                className="p-1 hover:bg-slate-100 rounded"
                                title={isUSA ? 'Find Pantone' : 'Buscar Pantone'}
                              >
                                <ExternalLink className="w-3 h-3 text-slate-400" />
                              </button>
                            </div>
                          </div>
                          {color.percentage && (
                            <div className="mt-1 bg-slate-100 rounded-full h-1.5">
                              <div 
                                className="bg-purple-500 h-full rounded-full"
                                style={{ width: `${color.percentage * 100}%` }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Quick Pantone Search */}
                  <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <p className="text-sm text-purple-700 flex items-center gap-2">
                      <Palette className="w-4 h-4" />
                      {isUSA 
                        ? 'Click on a color to copy its HEX code'
                        : 'Haz clic en un color para copiar su código HEX'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Fabric/Garment Analysis */}
            {result && result.analysis && (result.analysis.fabric_type || result.analysis.garment_type) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Layers className="w-5 h-5 text-blue-600" />
                    {isUSA ? 'Material Analysis' : 'Análisis de Material'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {result.analysis.fabric_type && (
                    <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                      <Layers className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="text-xs text-blue-600">{isUSA ? 'Fabric Type' : 'Tipo de Tela'}</p>
                        <p className="font-medium text-slate-800">{result.analysis.fabric_type}</p>
                      </div>
                    </div>
                  )}
                  
                  {result.analysis.garment_type && (
                    <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                      <Shirt className="w-5 h-5 text-emerald-600" />
                      <div>
                        <p className="text-xs text-emerald-600">{isUSA ? 'Garment Type' : 'Tipo de Prenda'}</p>
                        <p className="font-medium text-slate-800">{result.analysis.garment_type}</p>
                      </div>
                    </div>
                  )}
                  
                  {result.analysis.texture && (
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1">{isUSA ? 'Texture' : 'Textura'}</p>
                      <p className="text-sm text-slate-700">{result.analysis.texture}</p>
                    </div>
                  )}
                  
                  {result.analysis.pattern && (
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1">{isUSA ? 'Pattern' : 'Patrón'}</p>
                      <p className="text-sm text-slate-700">{result.analysis.pattern}</p>
                    </div>
                  )}
                  
                  {/* Description */}
                  <div className="p-3 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg border">
                    <p className="text-sm text-slate-700">
                      {isUSA ? result.analysis.description_en : result.analysis.description_es}
                    </p>
                  </div>
                  
                  {/* Search Keywords */}
                  {result.analysis.search_keywords?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {result.analysis.search_keywords.map((keyword, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {keyword}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex gap-2 pt-3 border-t">
                    {onSendToOptimizer && searchImage && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={sendToOptimizer}
                      >
                        <Wand2 className="w-4 h-4 mr-1" />
                        {isUSA ? 'Optimize Photo' : 'Optimizar Foto'}
                      </Button>
                    )}
                    <Button 
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={openAddToCatalog}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      {isUSA ? 'Add to Catalog' : 'Agregar al Catálogo'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right: Similar Products */}
          <Card className="h-fit">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5 text-orange-600" />
                {isUSA ? 'Similar Products in Catalog' : 'Productos Similares en Catálogo'}
              </CardTitle>
              <CardDescription>
                {result 
                  ? (isUSA 
                      ? `Found ${result.similar_products?.length || 0} similar products`
                      : `Se encontraron ${result.similar_products?.length || 0} productos similares`)
                  : (isUSA 
                      ? 'Upload an image to find similar products'
                      : 'Sube una imagen para encontrar productos similares')
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!result ? (
                <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                  <Search className="w-16 h-16 mb-4 opacity-30" />
                  <p className="text-center">
                    {isUSA 
                      ? 'Similar products will appear here after analysis' 
                      : 'Los productos similares aparecerán aquí después del análisis'}
                  </p>
                </div>
              ) : result.similar_products?.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                  <Package className="w-16 h-16 mb-4 opacity-30" />
                  <p className="text-center">
                    {isUSA 
                      ? 'No similar products found in the catalog' 
                      : 'No se encontraron productos similares en el catálogo'}
                  </p>
                  <p className="text-sm text-center mt-2">
                    {isUSA 
                      ? 'Would you like to add this item to your catalog?' 
                      : '¿Te gustaría agregar este artículo a tu catálogo?'}
                  </p>
                  <Button 
                    className="mt-4 bg-green-600 hover:bg-green-700"
                    onClick={openAddToCatalog}
                    disabled={!result?.analysis}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {isUSA ? 'Add to Catalog' : 'Agregar al Catálogo'}
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[600px] overflow-y-auto">
                  {result.similar_products.map((product, idx) => (
                    <div 
                      key={product.product_id || idx}
                      className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                    >
                      {product.image || product.image_url ? (
                        <img 
                          src={product.image || product.image_url}
                          alt={product.name}
                          className="w-full h-32 object-cover cursor-pointer"
                          onClick={() => setImageModal({ src: product.image || product.image_url, title: product.name })}
                        />
                      ) : (
                        <div className="w-full h-32 bg-slate-100 flex items-center justify-center">
                          <Image className="w-10 h-10 text-slate-300" />
                        </div>
                      )}
                      <div className="p-3">
                        <p className="font-medium text-sm line-clamp-1">{product.name}</p>
                        {product.company_name && (
                          <p className="text-xs text-slate-500">{product.company_name}</p>
                        )}
                        {product.similarity_reason && (
                          <p className="text-xs text-emerald-600 mt-1 line-clamp-2 italic">
                            ✓ {product.similarity_reason}
                          </p>
                        )}
                        <div className="flex items-center justify-between mt-2">
                          {product.price && (
                            <span className="text-sm font-semibold text-emerald-600">
                              ${product.price.toLocaleString()}
                            </span>
                          )}
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => window.open(`/product/${product.product_id}`, '_blank')}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            {isUSA ? 'View' : 'Ver'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-emerald-600" />
              {isUSA ? 'Search History' : 'Historial de Búsquedas'}
            </CardTitle>
            <CardDescription>
              {isUSA 
                ? 'Your previous visual searches and color analyses' 
                : 'Tus búsquedas visuales y análisis de colores anteriores'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingHistory ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : history.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <History className="w-16 h-16 mb-4 opacity-30" />
                <p className="text-center">
                  {isUSA ? 'No search history yet' : 'Aún no hay historial de búsquedas'}
                </p>
                <Button 
                  className="mt-4 bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => setActiveTab('search')}
                >
                  <Search className="w-4 h-4 mr-2" />
                  {isUSA ? 'Start searching' : 'Comenzar a buscar'}
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {history.map((item, idx) => (
                  <div 
                    key={item.search_id || idx}
                    className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => loadFromHistory(item)}
                  >
                    <div className="flex">
                      {/* Thumbnail image */}
                      <div className="w-24 h-24 flex-shrink-0 bg-slate-100">
                        {item.thumbnail_base64 ? (
                          <img 
                            src={`data:image/jpeg;base64,${item.thumbnail_base64}`}
                            alt="Search thumbnail"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Image className="w-8 h-8 text-slate-300" />
                          </div>
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 p-3">
                        {/* Colors preview */}
                        <div className="flex gap-1 mb-2">
                          {item.analysis?.colors?.slice(0, 5).map((color, i) => (
                            <div 
                              key={i}
                              className="w-5 h-5 rounded border border-slate-200"
                              style={{ backgroundColor: color.hex }}
                              title={`${color.name} (${color.hex})`}
                            />
                          ))}
                        </div>
                        
                        {/* Description */}
                        <p className="text-xs text-slate-700 line-clamp-2">
                          {isUSA 
                            ? item.analysis?.description_en 
                            : item.analysis?.description_es}
                        </p>
                        
                        {/* Meta info */}
                        <div className="flex items-center gap-2 mt-2 text-xs text-slate-500 flex-wrap">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(item.created_at)}
                          </span>
                          {item.analysis?.fabric_type && (
                            <Badge variant="outline" className="text-xs py-0">
                              {item.analysis.fabric_type}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Image Modal */}
      {imageModal && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setImageModal(null)}
        >
          <div className="relative max-w-4xl w-full" onClick={e => e.stopPropagation()}>
            <Button 
              variant="ghost" 
              size="icon"
              className="absolute -top-12 right-0 text-white hover:bg-white/20"
              onClick={() => setImageModal(null)}
            >
              <X className="w-6 h-6" />
            </Button>
            <div className="bg-white rounded-xl overflow-hidden">
              <img 
                src={imageModal.src}
                alt={imageModal.title}
                className="w-full max-h-[80vh] object-contain"
              />
              <div className="p-4 border-t">
                <p className="font-medium text-slate-900">{imageModal.title}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add to Catalog Dialog */}
      <Dialog open={showAddCatalog} onOpenChange={setShowAddCatalog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-green-600" />
              {isUSA ? 'Add to Catalog' : 'Agregar al Catálogo'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Preview image */}
            {searchPreview && (
              <div className="flex justify-center">
                <img 
                  src={searchPreview} 
                  alt="Product" 
                  className="h-32 object-contain rounded-lg border"
                />
              </div>
            )}

            {/* Product Name */}
            <div>
              <Label>{isUSA ? 'Product Name' : 'Nombre del Producto'} *</Label>
              <Input
                value={catalogForm.name}
                onChange={(e) => setCatalogForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder={isUSA ? 'E.g.: Blue Cotton Fabric' : 'Ej: Tela de Algodón Azul'}
                className="mt-1"
              />
            </div>

            {/* Description */}
            <div>
              <Label>{isUSA ? 'Description' : 'Descripción'}</Label>
              <Textarea
                value={catalogForm.description}
                onChange={(e) => setCatalogForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder={isUSA ? 'Product description...' : 'Descripción del producto...'}
                rows={3}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                {isUSA ? 'Pre-filled from AI analysis' : 'Pre-llenado del análisis de IA'}
              </p>
            </div>

            {/* Category */}
            <div>
              <Label>{isUSA ? 'Category' : 'Categoría'}</Label>
              <Select 
                value={catalogForm.category} 
                onValueChange={(v) => setCatalogForm(prev => ({ ...prev, category: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={isUSA ? 'Select category...' : 'Seleccionar categoría...'} />
                </SelectTrigger>
                <SelectContent>
                  {PRODUCT_CATEGORIES.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {isUSA ? cat.nameEn : cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Composition */}
            <div>
              <Label>{isUSA ? 'Composition / Material' : 'Composición / Material'}</Label>
              <Input
                value={catalogForm.composition}
                onChange={(e) => setCatalogForm(prev => ({ ...prev, composition: e.target.value }))}
                placeholder={isUSA ? 'E.g.: 100% Cotton' : 'Ej: 100% Algodón'}
                className="mt-1"
              />
            </div>

            {/* Colors */}
            <div>
              <Label className="flex items-center gap-2">
                <Palette className="w-4 h-4" />
                {isUSA ? 'Available Colors' : 'Colores Disponibles'}
              </Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {catalogForm.colors.map((hex, i) => (
                  <div 
                    key={i}
                    className="flex items-center gap-1 bg-slate-100 rounded-full px-2 py-1"
                  >
                    <div 
                      className="w-5 h-5 rounded-full border border-slate-300"
                      style={{ backgroundColor: hex }}
                    />
                    <span className="text-xs font-mono">{hex}</span>
                    <button 
                      onClick={() => removeColor(hex)}
                      className="p-0.5 hover:bg-slate-200 rounded-full"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              {result?.analysis?.colors && (
                <div className="mt-2">
                  <p className="text-xs text-slate-500 mb-1">
                    {isUSA ? 'Add from detected:' : 'Agregar de detectados:'}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {result.analysis.colors
                      .filter(c => !catalogForm.colors.includes(c.hex))
                      .map((color, i) => (
                        <button
                          key={i}
                          onClick={() => addColor(color.hex)}
                          className="w-6 h-6 rounded border border-slate-300 hover:scale-110 transition-transform"
                          style={{ backgroundColor: color.hex }}
                          title={`${color.name} (${color.hex})`}
                        />
                      ))}
                  </div>
                </div>
              )}
            </div>

            {/* Price & Unit */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{isUSA ? 'Price (MXN)' : 'Precio (MXN)'}</Label>
                <Input
                  type="number"
                  value={catalogForm.price}
                  onChange={(e) => setCatalogForm(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="0.00"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>{isUSA ? 'Unit' : 'Unidad'}</Label>
                <Select 
                  value={catalogForm.unit} 
                  onValueChange={(v) => setCatalogForm(prev => ({ ...prev, unit: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="metro">{isUSA ? 'Meter' : 'Metro'}</SelectItem>
                    <SelectItem value="kilo">{isUSA ? 'Kilo' : 'Kilo'}</SelectItem>
                    <SelectItem value="pieza">{isUSA ? 'Piece' : 'Pieza'}</SelectItem>
                    <SelectItem value="rollo">{isUSA ? 'Roll' : 'Rollo'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCatalog(false)}>
              {isUSA ? 'Cancel' : 'Cancelar'}
            </Button>
            <Button 
              onClick={handleSaveProduct}
              disabled={savingProduct || !catalogForm.name.trim()}
              className="bg-green-600 hover:bg-green-700"
            >
              {savingProduct ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <><Save className="w-4 h-4 mr-2" /> {isUSA ? 'Save Product' : 'Guardar Producto'}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VisualSearch;
